import type { ExpressionFactory, Transaction } from "@rocicorp/zero";
import { FEDERATED_API_KEY_NAME_PREFIX } from "./apiKeyPrefix";
import { type Schema, zql } from "./generatedDrizzleSchema";
import {
  type Permission,
  TENANT_READ_FALLBACK,
  TENANT_WRITE_FALLBACK,
} from "./rbac/permissions";
import {
  ALLOW_ALL_ROLE_IDS,
  BUILTIN_ROLE_IDS_GRANTING,
  type BuiltinRoleId,
} from "./rbac/roles";
import { userFacingError } from "./userFacingError";

export { userFacingError };

// A grant-holding identity distinct from the logged-in user. Present only on
// the server REST path when a request authenticates as a service account; the
// browser/Zero client never sets it, so `userId`-only behavior is unchanged.
export type Principal =
  | { kind: "user"; userId: string }
  | { kind: "serviceAccount"; serviceAccountId: string };

export type Context = {
  userId: string;
  tenantId: string;
  // When set, authorization runs against this principal instead of `userId`.
  // `userId` is retained for audit attribution (the human who owns the
  // credential). Defaults to acting as `userId` when absent.
  principal?: Principal;
  // A mutation calls this to attach curated detail (e.g. a before/after diff)
  // to its own audit-log entry — data that isn't in the raw inputs. Present
  // only on the server mutate path; absent when a shared mutator runs
  // optimistically on the client, so callers must tolerate its absence.
  recordAuditMetadata?: (metadata: Record<string, unknown>) => void;
};

export const NOT_FOUND_MESSAGES = {
  space: "Space not found",
  workflow: "Workflow not found",
  deletedWorkflow: "Workflow not found",
  branch: "Branch not found",
  execution: "Execution not found",
  connectedApp: "Connector not found",
  skill: "Skill not found",
  oauthConnection: "OAuth connection not found",
  ssoProvider: "SSO provider not found",
  commit: "Commit not found",
  run: "Run not found",
  chat: "Chat not found",
  attachment: "Attachment not found",
  user: "User not found",
  userGroup: "User group not found",
  serviceAccount: "Service account not found",
  serviceAccountFederation: "Federation not found",
  oidcProvider: "OIDC provider not found",
  oauthGrant: "OAuth grant not found",
  role: "Role not found",
  customRole: "Custom role not found",
  aiProvider: "AI provider not found",
  chatEntry: "Chat entry not found",
  connectionClaim: "Claim not found",
  grantRequest: "Share request not found",
  spaceVolume: "Volume not found",
  volume: "Volume not found",
  network: "Network not found",
  egressRoute: "Route not found",
  egressRule: "Egress rule not found",
  auditLogDestination: "Audit log destination not found",
  featureFlag: "Feature flag not found",
  aiBudget: "Budget not found",
  connectorUsageBudget: "Budget not found",
  connectorUsagePrice: "Price not found",
} as const;

// ---------------------------------------------------------------------------
// Grant-based authorization
// ---------------------------------------------------------------------------

export const principalMatch =
  (userId: string) =>
  ({
    or,
    cmp,
    exists: e,
  }: Parameters<ExpressionFactory<"roleGrants", Schema>>[0]) =>
    or(
      cmp("userId", userId),
      e("groupMembershipGrants", (mg) => mg.where("userId", userId))
    );

const roleMatches =
  (permission: Permission): ExpressionFactory<"roleGrants", Schema> =>
  ({ or, cmp, exists: e }) => {
    const builtinIds: readonly BuiltinRoleId[] = [
      ...(BUILTIN_ROLE_IDS_GRANTING[permission] ?? []),
      ...ALLOW_ALL_ROLE_IDS,
    ];
    return or(
      ...(builtinIds.length > 0
        ? [cmp("builtinRoleId", "IN", builtinIds)]
        : []),
      e("customRole", (cr) =>
        cr.whereExists("rolePermissions", (rp) =>
          rp.where("permission", permission)
        )
      )
    );
  };

// Checks for a grant of `permission` (allow_all included) reachable via the
// given relation: `roleGrantsAsScope` for grants on the row itself, or
// `tenantRoleGrantsAsScope` (row.tenant_id → grant.scope_id) for tenant-scoped
// grants, which are keyed by the real tenants.id.
const grantViaRelationExists =
  (
    ctx: Context,
    permission: Permission,
    relation: "roleGrantsAsScope" | "tenantRoleGrantsAsScope"
  ) =>
  ({ or, exists }: Parameters<ExpressionFactory<"spaces", Schema>>[0]) => {
    const match = roleMatches(permission);
    // A service-account principal holds its own grants directly and is never a
    // member of a user group, so a single `service_account_id` match suffices —
    // authorizing as the SA, not the owning human, is the whole point of
    // `ctx.principal`.
    if (ctx.principal?.kind === "serviceAccount") {
      const serviceAccountId = ctx.principal.serviceAccountId;
      return exists(relation, (rg) =>
        rg.where(match).where("serviceAccountId", serviceAccountId)
      );
    }
    // Split the (direct-user OR group-member) principal check into two separate
    // EXISTS so the direct-user branch can index-seek `(scope_id, user_id)`
    // instead of full-scanning every grant in scope per row. `EXISTS(role ∧ (a ∨
    // b))` ≡ `EXISTS(role ∧ a) ∨ EXISTS(role ∧ b)`, so this is equivalent.
    return or(
      exists(relation, (rg) => rg.where(match).where("userId", ctx.userId)),
      exists(relation, (rg) =>
        rg
          .where(match)
          .whereExists("groupMembershipGrants", (mg) =>
            mg.where("userId", ctx.userId)
          )
      )
    );
  };

const grantExists = (ctx: Context, permission: Permission) =>
  grantViaRelationExists(ctx, permission, "roleGrantsAsScope");

const tenantRowInCtx =
  (ctx: Context) => (eb: Parameters<ExpressionFactory<"spaces", Schema>>[0]) =>
    eb.cmp("tenantId", ctx.tenantId);

// Like `grantExists`, but checks a tenant-scoped grant of `permission`.
export const tenantGrantExists =
  (ctx: Context, permission: Permission) =>
  (eb: Parameters<ExpressionFactory<"spaces", Schema>>[0]) =>
    eb.and(
      tenantRowInCtx(ctx)(eb),
      grantViaRelationExists(ctx, permission, "tenantRoleGrantsAsScope")(eb)
    );

// `allow_all` is only ever granted to users directly, never to user groups (the
// group-grant path rejects it via `assertNoAllowAll`), so there is no group
// membership to check — a direct `userId` match suffices and index-seeks.
const tenantAdminExists =
  (ctx: Context) => (eb: Parameters<ExpressionFactory<"spaces", Schema>>[0]) =>
    eb.and(
      tenantRowInCtx(ctx)(eb),
      eb.exists("tenantRoleGrantsAsScope", (rg) =>
        rg
          .where("builtinRoleId", "IN", [...ALLOW_ALL_ROLE_IDS])
          .where("userId", ctx.userId)
      )
    );

const canDiscoverSpace =
  (ctx: Context): ExpressionFactory<"spaces", Schema> =>
  (eb) =>
    eb.and(
      eb.or(
        eb.cmp("defaultJoinBuiltinRoleId", "IS NOT", null),
        eb.cmp("defaultJoinCustomRoleId", "IS NOT", null)
      ),
      eb.cmp("mode", "!=", "personal"),
      tenantRowInCtx(ctx)(eb)
    );

// Read access to a scoped resource: a direct or inherited grant of the scoped
// read permission, or the tenant-wide read that subsumes it. The fallback comes
// from the shared `TENANT_READ_FALLBACK` map, so this and the Drizzle
// `accessExists` stay in lockstep. Typed against `"spaces"` for the `eb`
// builder; the per-resource `canRead*` helpers cast to their own table (the
// builder is structurally identical across tables).
const canReadScope =
  (ctx: Context, permission: Permission) =>
  (eb: Parameters<ExpressionFactory<"spaces", Schema>>[0]) => {
    const scoped = grantExists(ctx, permission)(eb);
    const fallback = TENANT_READ_FALLBACK[permission];
    return fallback
      ? eb.or(scoped, tenantGrantExists(ctx, fallback)(eb))
      : scoped;
  };

export const canReadSpace = (
  ctx: Context
): ExpressionFactory<"spaces", Schema> => canReadScope(ctx, "space:read");

// Write access to a space: a direct space:write grant, tenant admin, or the
// tenant-wide write that subsumes it (tenant:spaces_manage via
// TENANT_WRITE_FALLBACK). The fallback keeps this in lockstep with the Drizzle
// `hasAccess` and the mutator gate, so a space_manager_all holder isn't shown
// spaces as read-only in the UI while being authorized server-side.
export const canWriteSpace =
  (ctx: Context): ExpressionFactory<"spaces", Schema> =>
  (eb) => {
    const fallback = TENANT_WRITE_FALLBACK["space:write"];
    return eb.or(
      grantExists(ctx, "space:write")(eb),
      tenantAdminExists(ctx)(eb),
      ...(fallback ? [tenantGrantExists(ctx, fallback)(eb)] : [])
    );
  };

const canReadSpaceAsMember =
  (ctx: Context): ExpressionFactory<"spaces", Schema> =>
  (eb) =>
    grantExists(ctx, "space:read")(eb);

export const canReadSpaceAsMemberOrDiscover =
  (ctx: Context): ExpressionFactory<"spaces", Schema> =>
  (eb) =>
    eb.or(grantExists(ctx, "space:read")(eb), canDiscoverSpace(ctx)(eb));

export const canReadConnector = (
  ctx: Context
): ExpressionFactory<"connectedApps", Schema> =>
  canReadScope(ctx, "connected_app:read") as unknown as ExpressionFactory<
    "connectedApps",
    Schema
  >;

export const canUseConnector =
  (ctx: Context): ExpressionFactory<"connectedApps", Schema> =>
  (eb) =>
    eb.or(
      (
        grantExists(ctx, "connected_app:use") as unknown as ExpressionFactory<
          "connectedApps",
          Schema
        >
      )(eb),
      (
        tenantAdminExists(ctx) as unknown as ExpressionFactory<
          "connectedApps",
          Schema
        >
      )(eb)
    );

// A pending grant request is visible to its requester and to anyone who could
// review it — i.e. anyone who could share the connector directly: a holder of
// the per-connector `connected_app:share`, or a tenant admin via allow_all.
// (Not tenant `connected_app_share`, which only governs sharing your own
// connectors.) The approve/dismiss mutators gate on the same permission.
export const canReviewGrantRequest =
  (ctx: Context): ExpressionFactory<"grantRequests", Schema> =>
  (eb) =>
    eb.or(
      eb.cmp("requestedByUserId", ctx.userId),
      (
        tenantAdminExists(ctx) as unknown as ExpressionFactory<
          "grantRequests",
          Schema
        >
      )(eb),
      eb.exists("scopeAsConnector", (c) =>
        c.where(
          grantExists(
            ctx,
            "connected_app:share"
          ) as unknown as ExpressionFactory<"connectedApps", Schema>
        )
      )
    );

export const canReadSkill = (
  ctx: Context
): ExpressionFactory<"skills", Schema> =>
  canReadScope(ctx, "skill:read") as unknown as ExpressionFactory<
    "skills",
    Schema
  >;

export const canReadWorkflow =
  (ctx: Context): ExpressionFactory<"workflows", Schema> =>
  ({ and, cmp, exists }) =>
    and(
      cmp("deletedAt", "IS", null),
      exists("space", (s) => s.where(canReadSpace(ctx)))
    );

export const canReadDeletedWorkflow =
  (ctx: Context): ExpressionFactory<"workflows", Schema> =>
  ({ and, cmp, exists }) =>
    and(
      cmp("deletedAt", "IS NOT", null),
      exists("space", (s) => s.where(canReadSpace(ctx)))
    );

export const canReadBranch =
  (ctx: Context): ExpressionFactory<"workflowBranches", Schema> =>
  ({ exists }) =>
    exists("workflow", (w) => w.where(canReadWorkflow(ctx)));

export const canReadRun =
  (ctx: Context): ExpressionFactory<"workflowRuns", Schema> =>
  ({ exists }) =>
    exists("workflow", (w) => w.where(canReadWorkflow(ctx)));

const canReadWorkflowAsMember =
  (ctx: Context): ExpressionFactory<"workflows", Schema> =>
  ({ exists }) =>
    exists("space", (s) => s.where(canReadSpaceAsMember(ctx)));

export const canReadWorkflowView =
  (ctx: Context): ExpressionFactory<"workflowViews", Schema> =>
  ({ exists }) =>
    exists("workflow", (w) => w.where(canReadWorkflowAsMember(ctx)));

// Route views back the tenant-wide Links page, which surfaces links from spaces
// the viewer isn't a member of; mirror canReadRouteFavorite (not the
// member-scoped canReadWorkflowView) so a link opened there still appears in
// Recents.
export const canReadRouteView =
  (ctx: Context): ExpressionFactory<"routeViews", Schema> =>
  ({ exists }) =>
    exists("workflow", (w) => w.where(canReadWorkflow(ctx)));

export const canReadWorkflowFavorite =
  (ctx: Context): ExpressionFactory<"workflowFavorites", Schema> =>
  ({ exists }) =>
    exists("workflow", (w) => w.where(canReadWorkflow(ctx)));

export const canReadRouteFavorite =
  (ctx: Context): ExpressionFactory<"routeFavorites", Schema> =>
  ({ exists }) =>
    exists("workflow", (w) => w.where(canReadWorkflow(ctx)));

export const canReadDuplicateWorkflowReview =
  (ctx: Context): ExpressionFactory<"duplicateWorkflowReviews", Schema> =>
  ({ and, exists }) =>
    and(
      exists("workflowA", (w) => w.where(canReadWorkflow(ctx))),
      exists("workflowB", (w) => w.where(canReadWorkflow(ctx)))
    );

export const canReadDuplicateWorkflowPair =
  (ctx: Context): ExpressionFactory<"duplicateWorkflowPairs", Schema> =>
  ({ and, exists }) =>
    and(
      exists("workflowA", (w) => w.where(canReadWorkflow(ctx))),
      exists("workflowB", (w) => w.where(canReadWorkflow(ctx)))
    );

export const canReadNetwork = (
  ctx: Context
): ExpressionFactory<"networks", Schema> =>
  canReadScope(ctx, "network:read") as unknown as ExpressionFactory<
    "networks",
    Schema
  >;

export const canReadEgressRule =
  (ctx: Context): ExpressionFactory<"egressRules", Schema> =>
  (eb) =>
    (
      tenantGrantExists(
        ctx,
        "tenant:egress_rules_manage"
      ) as unknown as ExpressionFactory<"egressRules", Schema>
    )(eb);

export const canReadServiceAccount = (
  ctx: Context
): ExpressionFactory<"serviceAccounts", Schema> =>
  canReadScope(ctx, "service_account:read") as unknown as ExpressionFactory<
    "serviceAccounts",
    Schema
  >;

export const canReadSsoProvider =
  (ctx: Context): ExpressionFactory<"ssoProvider", Schema> =>
  (eb) =>
    (
      tenantGrantExists(
        ctx,
        "tenant:sso_manage"
      ) as unknown as ExpressionFactory<"ssoProvider", Schema>
    )(eb);

export const canReadOidcProvider =
  (ctx: Context): ExpressionFactory<"oidcProviders", Schema> =>
  (eb) =>
    (
      tenantGrantExists(
        ctx,
        "tenant:oidc_providers_manage"
      ) as unknown as ExpressionFactory<"oidcProviders", Schema>
    )(eb);

export const canReadAuditLog =
  (ctx: Context): ExpressionFactory<"auditLogs", Schema> =>
  (eb) =>
    (
      tenantGrantExists(
        ctx,
        "tenant:audit_logs_read"
      ) as unknown as ExpressionFactory<"auditLogs", Schema>
    )(eb);

export const canReadAuditLogDestination =
  (ctx: Context): ExpressionFactory<"auditLogDestinations", Schema> =>
  (eb) =>
    (
      tenantGrantExists(
        ctx,
        "tenant:audit_logs_manage"
      ) as unknown as ExpressionFactory<"auditLogDestinations", Schema>
    )(eb);

export const canReadApiKey =
  (ctx: Context): ExpressionFactory<"apikey", Schema> =>
  (eb) =>
    eb.or(
      (
        tenantGrantExists(
          ctx,
          "tenant:users_manage"
        ) as unknown as ExpressionFactory<"apikey", Schema>
      )(eb),
      eb.cmp("referenceId", ctx.userId)
    );

// Keeps a service account's key lists (the modal's serviceAccountKeys and the
// row's related apiKeys badges) scoped to human-managed keys by hiding the
// auto-minted credentials: UI-issued sessions (`session:<id>` from
// createServiceAccountSession) and federation mints (from the federated-token
// exchange). Keep the session name in sync if it changes.
export const isHumanManagedServiceAccountKey =
  (): ExpressionFactory<"apikey", Schema> =>
  ({ and, or, cmp }) =>
    or(
      cmp("name", "IS", null),
      and(
        cmp("name", "NOT LIKE", "session:%"),
        cmp("name", "NOT LIKE", `${FEDERATED_API_KEY_NAME_PREFIX}%`)
      )
    );

export const canReadPersonalChats =
  (ctx: Context): ExpressionFactory<"personalChats", Schema> =>
  ({ or, cmp, exists }) =>
    or(
      cmp("userId", ctx.userId),
      exists("user", (u) =>
        u.where(
          tenantGrantExists(
            ctx,
            "tenant:personal_chats_read"
          ) as unknown as ExpressionFactory<"user", Schema>
        )
      )
    );

export const canReadPersonalChat =
  (ctx: Context): ExpressionFactory<"connectedAppUsages", Schema> =>
  ({ exists }) =>
    exists("personalChat", (pc) => pc.where(canReadPersonalChats(ctx)));

export function branchQuery(ctx: Context, permission: Permission) {
  return zql.workflowBranches.where(({ exists }) =>
    exists("workflow", (w) =>
      w.where(({ and, cmp, exists: e }) =>
        and(
          cmp("deletedAt", "IS", null),
          e("space", (s) =>
            s.where((eb) =>
              eb.or(
                grantExists(ctx, permission)(eb),
                tenantAdminExists(ctx)(eb)
              )
            )
          )
        )
      )
    )
  );
}

export async function zqlFetchBranch(
  tx: Pick<Transaction<Schema>, "run">,
  ctx: Context,
  id: string,
  permission: Permission
) {
  const [row] = await tx.run(branchQuery(ctx, permission).where("id", id));
  return row ?? null;
}
